import React, { useEffect, useRef } from 'react';
import { Heart, Stethoscope, Microscope, Pill, Activity, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Services.css';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const Services = () => {
    const { t } = useTranslation();
    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef(null);

    const services = [
        {
            icon: Heart,
            titleKey: 'services.items.cardiology.title',
            descriptionKey: 'services.items.cardiology.description',
            color: '#ef4444',
        },
        {
            icon: Sparkles,
            titleKey: 'services.items.neurology.title',
            descriptionKey: 'services.items.neurology.description',
            color: '#8b5cf6',
        },
        {
            icon: Stethoscope,
            titleKey: 'services.items.general.title',
            descriptionKey: 'services.items.general.description',
            color: '#2563eb',
        },
        {
            icon: Microscope,
            titleKey: 'services.items.laboratory.title',
            descriptionKey: 'services.items.laboratory.description',
            color: '#14b8a6',
        },
        {
            icon: Pill,
            titleKey: 'services.items.pharmacy.title',
            descriptionKey: 'services.items.pharmacy.description',
            color: '#10b981',
        },
        {
            icon: Activity,
            titleKey: 'services.items.preventive.title',
            descriptionKey: 'services.items.preventive.description',
            color: '#f59e0b',
        },
    ];

    useEffect(() => {
        // Animate title
        if (titleRef.current) {
            gsap.fromTo(
                titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        // Animate cards
        if (cardsRef.current) {
            const cards = cardsRef.current.children;
            Array.from(cards).forEach((card, index) => {
                gsap.fromTo(
                    card,
                    { opacity: 0, y: 60, scale: 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: 'power3.out',
                        delay: index * 0.1,
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );

                // Hover effect
                card.addEventListener('mouseenter', () => {
                    gsap.to(card, {
                        y: -10,
                        scale: 1.03,
                        duration: 0.3,
                        ease: 'power2.out',
                    });
                });

                card.addEventListener('mouseleave', () => {
                    gsap.to(card, {
                        y: 0,
                        scale: 1,
                        duration: 0.3,
                        ease: 'power2.out',
                    });
                });
            });
        }

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => {
                if (
                    (sectionRef.current && trigger.vars.trigger === sectionRef.current) ||
                    (titleRef.current && trigger.vars.trigger === titleRef.current)
                ) {
                    trigger.kill();
                }
            });
        };
    }, []);

    return (
        <section id="services" ref={sectionRef} className="services">
            <div className="services-container">
                <div className="services-header">
                    <div className="section-badge">{t('services.title')}</div>
                    <h2 ref={titleRef} className="section-title">
                        {t('services.subtitle')}
                    </h2>
                </div>
                <div ref={cardsRef} className="services-grid">
                    {services.map((service, index) => {
                        const IconComponent = service.icon;
                        return (
                            <div key={index} className="service-card card-hover">
                                <div className="service-icon-wrapper" style={{ background: `${service.color}15` }}>
                                    <IconComponent size={32} style={{ color: service.color }} />
                                </div>
                                <h3 className="service-title">{t(service.titleKey)}</h3>
                                <p className="service-description">{t(service.descriptionKey)}</p>
                                <a href="#contact" className="service-link">
                                    Learn More <span>→</span>
                                </a>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Services;

